/**
 * Filter menu items based on module/submodule permissions
 * Updated to use Code property instead of Name for more reliable matching
 */

/**
 * Filter menu items recursively based on permissions
 * @param {Array} menuItems - Menu items to filter
 * @param {Array} permissions - Module permissions with CanView property
 * @returns {Array} Filtered menu items
 */
export function filterMenuItems(menuItems, permissions) {
    return menuItems.map(item => {
        if (item._children) {
            item._children = filterMenuItems(item._children, permissions)
        }

        // Skip items without module code
        if (!item.moduleCode) {
            return item;
        }

        // Check if user has CanView permission for this module
        const hasPermission = permissions.some(
            permission => permission.Module?.Code === item.moduleCode && permission.CanView
        );

        return hasPermission ? item : null;
    }).filter(item => item !== null)
}

/**
 * Filter menu configuration based on role permissions (uses Code instead of Name)
 * @param {Array} menuConfig - Menu configuration
 * @param {Array} roleModulePermissions - Module-level permissions
 * @param {Array} roleSubModulePermissions - SubModule-level permissions
 * @param {Boolean} isSuperAdmin - Whether user is super admin
 * @returns {Array} Filtered menu
 */
export function filterMenuByName(menuConfig, roleModulePermissions, roleSubModulePermissions, isSuperAdmin = false) {
    console.log('=== filterMenuByName called ===');
    console.log('isSuperAdmin:', isSuperAdmin);
    console.log('roleModulePermissions:', roleModulePermissions);
    console.log('roleSubModulePermissions:', roleSubModulePermissions);

    // SuperAdmin sees everything
    if (isSuperAdmin) {
        console.log('User is SuperAdmin - returning all menu items');
        return menuConfig;
    }

    // Build lookup maps for faster access using Code property
    // Note: API returns camelCase properties (canView, module.code)
    const allowedModuleCodes = new Set(
        roleModulePermissions
            .filter(permission => permission.canView || permission.CanView)
            .map(permission => permission.module?.code || permission.Module?.Code)
            .filter(code => code) // Remove undefined/null
    );

    const allowedSubModuleCodes = new Set(
        roleSubModulePermissions
            .filter(permission => permission.canView || permission.CanView)
            .map(permission => permission.subModule?.code || permission.SubModule?.Code)
            .filter(code => code) // Remove undefined/null
    );

    console.log('Allowed Module Codes:', Array.from(allowedModuleCodes));
    console.log('Allowed SubModule Codes:', Array.from(allowedSubModuleCodes));

    const filteredMenu = menuConfig.map(menu => {
        console.log(`Processing menu item: ${menu.name}, moduleCode: ${menu.moduleCode}, subModuleCode: ${menu.subModuleCode}`);

        // Check if menu item has moduleCode and user has permission
        if (menu.moduleCode && allowedModuleCodes.has(menu.moduleCode)) {
            console.log(`  ✓ Menu item "${menu.name}" ALLOWED - has moduleCode ${menu.moduleCode}`);
            // If it's a top-level module and is allowed
            if (menu._children) {
                menu._children = menu._children.filter(subMenu => {
                    // Keep child if it has no subModuleCode OR if user has permission
                    const allowed = !subMenu.subModuleCode || allowedSubModuleCodes.has(subMenu.subModuleCode);
                    console.log(`    Child "${subMenu.name}" (subModuleCode: ${subMenu.subModuleCode}): ${allowed ? 'ALLOWED' : 'DENIED'}`);
                    return allowed;
                });
            }
            return menu;
        } else if (menu._children) {
            console.log(`  Dropdown menu "${menu.name}" - filtering children`);
            // If it's a dropdown menu without moduleCode, filter children based on their permissions
            menu._children = menu._children.map(subMenu => {
                const moduleAllowed = subMenu.moduleCode && allowedModuleCodes.has(subMenu.moduleCode);
                const subAllowed = subMenu.subModuleCode && allowedSubModuleCodes.has(subMenu.subModuleCode);
                if (!moduleAllowed && !subAllowed) {
                    console.log(`    Child "${subMenu.name}" (moduleCode: ${subMenu.moduleCode}, subModuleCode: ${subMenu.subModuleCode}): DENIED`);
                    return null;
                }
                // If this allowed child is itself a module dropdown with its own
                // children, gate those grandchildren by SUBMODULE permission too —
                // otherwise an allowed module would show every submodule regardless
                // of its CanView (e.g. Reports showing Sales Report when denied).
                if (subMenu._children) {
                    subMenu._children = subMenu._children.filter(
                        gc => !gc.subModuleCode || allowedSubModuleCodes.has(gc.subModuleCode)
                    );
                    if (subMenu._children.length === 0) {
                        console.log(`    Child dropdown "${subMenu.name}": REMOVED (no visible submodules)`);
                        return null;
                    }
                }
                console.log(`    Child "${subMenu.name}" (moduleCode: ${subMenu.moduleCode}, subModuleCode: ${subMenu.subModuleCode}): ALLOWED`);
                return subMenu;
            }).filter(Boolean);
            // Keep parent menu if it has at least one visible child
            const keep = menu._children.length > 0;
            console.log(`  Dropdown "${menu.name}": ${keep ? 'KEPT' : 'REMOVED'} (children count: ${menu._children.length})`);
            return keep ? menu : null;
        }

        // Menu item without moduleCode and without children - keep it (e.g., Dashboard)
        const keep = !menu.moduleCode;
        console.log(`  Menu item "${menu.name}" without moduleCode: ${keep ? 'KEPT' : 'REMOVED'}`);
        return keep ? menu : null;
    }).filter(menu => menu && (!menu._children || menu._children.length > 0));

    console.log('Final filtered menu:', filteredMenu);
    return filteredMenu;
}
  

  